import { useEffect } from 'react';
import * as yup from 'yup';
import type { FormDefinition } from '@gov-design-system-ce/react-form-core';
import {
	getFormDefinition,
	useGovForm,
} from '@gov-design-system-ce/react-form-core';
import type { DatovkaApiType } from '@gov-nx/api';
import { useAuthorizationConsentYupSchema } from '@gov-nx/core/hooks';
import { useLocale } from '@gov-nx/core/service';
import { getValues, TypeOfSchool } from '@gov-nx/core/types';
import type { AutocompleteDefinition } from '@gov-nx/utils/common';
import {
	useAutocompleteDefinition,
	useDataBoxShape,
	yearOptions,
} from '@gov-nx/utils/common';
import type { ServiceCode } from '../definitions/codes';
import type { DataRequest, FormData } from './service.types';

export interface FormInstanceProps {
	code: ServiceCode;
	dataBoxTypes: DatovkaApiType['typSchranky'][];
	isDisabled: boolean;
	requiresConsent: boolean;
}

export function FormInstance({
	code,
	dataBoxTypes,
	isDisabled,
	requiresConsent,
}: FormInstanceProps): {
	fields: { school: AutocompleteDefinition['field'] };
	formDefinition: FormDefinition<FormData>;
} {
	const { getDataBoxShape, getDataBoxDefaultValues } = useDataBoxShape({
		types: dataBoxTypes,
	});
	const { t } = useLocale(code);
	const consentSchema = useAuthorizationConsentYupSchema({
		isConsentRequired: requiresConsent,
	});
	const { datovaSchrankaId, ...dataBoxAdditional } = getDataBoxShape({
		isRequired: false,
	});

	const school = useAutocompleteDefinition({
		name: 'skola',
		label: t('formular.pole.nazev-skoly'),
		placeholder: t('formular.placeholders.nazev-skoly'),
		requiredMessage: t('formular.validace.skola-nazev.povinny'),
		isDisabled,
	});

	const formSchema = yup
		.object({
			...dataBoxAdditional,
			odesilatelDSId: datovaSchrankaId,
			skola: school.schema,
			adresatDSId: yup.string().optional(),

			fakulta: yup.string().when('skolaTyp', ([skolaTyp]) => {
				if (skolaTyp === TypeOfSchool.VS) {
					return yup.string().required(t('formular.validace.fakulta.povinny'));
				}
				return yup.string().optional();
			}),
			skolniRok: yup
				.string()
				.required(t('formular.validace.skolni-rok.vyberte')),
			studijniProgram: yup
				.string()
				.required(t('formular.validace.studijni-program.povinny')),
			formaStudia: yup.string().optional(),
			skolaTyp: yup
				.string()
				.oneOf(getValues(TypeOfSchool))
				.required(t('formular.validace.skola.vyberte')),
			druhStudia: yup.string().optional(),
		})
		.shape({ __souhlas: consentSchema })
		.required();

	const dataBoxDefaultValues = getDataBoxDefaultValues();

	const formMethods = useGovForm<FormData>({
		formSchema,
		defaultValues: {
			odesilatelDSId: dataBoxDefaultValues.datovaSchrankaId,
			naseCisloJednaci: dataBoxDefaultValues.naseCisloJednaci,
			naseSpisovaZnacka: dataBoxDefaultValues.naseSpisovaZnacka,
			adresatDSId: undefined,

			skola: school.defaultValue,
			fakulta: undefined,
			skolniRok: yearOptions()[0].value,
			studijniProgram: undefined,
			formaStudia: undefined,
			skolaTyp: undefined,
			druhStudia: undefined,
		},
	});

	const type = formMethods.watch('skolaTyp');

	useEffect(() => {
		formMethods.setValue('skola', undefined);
	}, [formMethods, type]);

	return {
		fields: {
			school: school.field,
		},
		formDefinition: getFormDefinition({ formMethods, formSchema }),
	};
}

export const prepareSubmitData = (data: FormData): DataRequest => ({
	params: {
		adresatDSId: data.skola?.selected?.datovaSchrankaId,
		naseCisloJednaci: data.naseCisloJednaci,
		naseSpisovaZnacka: data.naseSpisovaZnacka,
		odesilatelDSId: data.odesilatelDSId,
	},
	body: {
		kodPodani: 'POTVRZENI_O_STUDIU',
		fakulta: data.fakulta,
		druhStudia: data.druhStudia,
		formaStudia: data.formaStudia,
		skolaNazev: `${data.skola?.selected?.nazev} (${data.skola?.selected?.datovaSchrankaId})`,
		skolaDatovaSchrankaId: data.skola?.selected?.datovaSchrankaId ?? '',
		skolniRok: data.skolniRok,
		skolaTyp: data.skolaTyp,
		studijniProgram: data.studijniProgram,
	},
});
